import { cn } from '@newfront-insurance/core-ui/v2';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { ComponentType, ReactNode, Ref } from 'react';

export type SkillListVariant = 'default' | 'compact';

const styles = {
  default: {
    categoryHeader: 'bg-steel-100 px-3.5 py-2.5',
    categoryTitle: 'text-steel-500 text-sm font-semibold',
    categoryCount: 'text-steel-400 text-sm',
    itemContainer: 'hover:bg-brand-100',
    itemContainerSelected: 'bg-brand-100',
    itemContainerExpanded: 'bg-brand-100',
    itemPadding: 'px-3.5 py-2.5 gap-3',
    iconContainer: 'size-9 bg-brand-100',
    iconContainerExpanded: 'border-steel-200 border',
    icon: 'size-[18px] text-brand-400',
    handleText: 'text-steel-500 text-sm font-semibold',
    descriptionText: 'text-steel-400 text-sm',
    chevronSize: 'size-4',
    chevronColor: 'text-steel-400 hover:text-steel-500',
    expandedContainer: 'mx-3.5 mb-2.5 bg-steel-50 border-steel-75',
    expandedLabel: 'text-steel-400 text-sm font-semibold tracking-[1.2px] uppercase',
    expandedContent: 'bg-steel-100',
    expandedText: 'text-steel-400 text-sm leading-4',
    titleText: 'text-steel-500 text-sm font-semibold',
    subLabelText: 'text-steel-400 text-xs font-semibold tracking-[1.2px] uppercase',
    exampleHandle: 'text-brand-300 font-mono',
  },
  compact: {
    categoryHeader: 'bg-steel-100 px-3.5 py-2.5',
    categoryTitle: 'text-steel-500 text-[12px] font-semibold',
    categoryCount: 'text-steel-400 text-[12px]',
    itemContainer: 'border-steel-75 bg-white border-b hover:bg-brand-100',
    itemContainerSelected: 'bg-brand-100',
    itemContainerExpanded: 'bg-brand-100',
    itemPadding: 'px-2.5 py-2.5 gap-2.5 items-start',
    iconContainer: 'bg-brand-100 size-9',
    iconContainerExpanded: 'border-steel-200 border',
    icon: 'text-brand-400 size-[18px]',
    handleText: 'text-brand-400 text-[12px] font-semibold',
    descriptionText: 'text-steel-400 text-[12px] leading-4',
    chevronSize: 'size-3',
    chevronColor: 'text-steel-400 hover:text-steel-500',
    expandedContainer: 'mx-2.5 mb-2.5 bg-steel-50 border-steel-75',
    expandedLabel: 'text-[12px] font-semibold tracking-[1.2px] text-steel-400 uppercase',
    expandedContent: 'bg-steel-100',
    expandedText: 'text-[12px] leading-4 text-steel-400',
    titleText: 'text-[12px] font-semibold text-steel-500',
    subLabelText: 'text-[11px] font-semibold tracking-[1.2px] text-steel-400 uppercase',
    exampleHandle: 'text-brand-300 font-mono',
  },
} as const;

export function SkillCategorySection({
  title,
  countLabel,
  variant = 'default',
  children,
}: {
  title: string;
  countLabel: string;
  variant?: SkillListVariant;
  children: ReactNode;
}) {
  const s = styles[variant];

  return (
    <div>
      <div className={cn('flex items-center justify-between', s.categoryHeader)}>
        <span className={s.categoryTitle}>{title}</span>
        <span className={s.categoryCount}>{countLabel}</span>
      </div>
      {children}
    </div>
  );
}

export function SkillListItem({
  title,
  handle,
  description,
  variant = 'default',
  isExpanded,
  isSelected = false,
  isDisabled = false,
  onPrimaryAction,
  primaryActionAriaLabel,
  primaryActionRef,
  icon: Icon,
  inlineActions,
  trailingAction,
  expandedContent,
}: {
  title?: string;
  handle: string;
  description: string;
  variant?: SkillListVariant;
  isExpanded: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  onPrimaryAction: () => void;
  primaryActionAriaLabel?: string;
  primaryActionRef?: Ref<HTMLButtonElement>;
  icon?: ComponentType<{ className?: string }>;
  inlineActions?: ReactNode;
  trailingAction?: ReactNode;
  expandedContent?: ReactNode;
}) {
  const s = styles[variant];

  return (
    <div
      className={cn(
        'group flex w-full flex-col transition-colors',
        s.itemContainer,
        isExpanded && s.itemContainerExpanded,
        isSelected && !isExpanded && s.itemContainerSelected,
        isDisabled && 'pointer-events-none opacity-50',
      )}
    >
      <div className={cn('flex w-full items-center', s.itemPadding)}>
        {Icon ? (
          <div
            className={cn(
              'flex shrink-0 items-center justify-center rounded-sm',
              s.iconContainer,
              isExpanded && s.iconContainerExpanded,
            )}
          >
            <Icon className={s.icon} />
          </div>
        ) : null}

        <div className="flex min-w-0 flex-1 items-center gap-x-2">
          <div className="flex min-w-0 flex-1 flex-col gap-y-1">
            <button
              ref={primaryActionRef}
              type="button"
              className="min-w-0 text-left"
              onClick={onPrimaryAction}
              aria-label={primaryActionAriaLabel}
              aria-expanded={primaryActionAriaLabel ? isExpanded : undefined}
              aria-disabled={isDisabled}
            >
              {title ? <div className={s.titleText}>{title}</div> : null}
              <span className={s.handleText}>{handle}</span>
            </button>

            <div
              className={cn(
                'min-w-0 text-left',
                primaryActionAriaLabel ? 'cursor-pointer' : undefined,
                s.descriptionText,
                variant === 'default' && 'truncate',
              )}
              onClick={onPrimaryAction}
            >
              {description}
            </div>
          </div>

          {inlineActions ? <div className="flex shrink-0 items-center gap-1">{inlineActions}</div> : null}
          {trailingAction ? <div className="flex shrink-0 items-center gap-1">{trailingAction}</div> : null}
        </div>
      </div>

      {isExpanded && expandedContent ? (
        <div className={cn('flex flex-col gap-2.5 rounded-[5px] border p-2', s.expandedContainer)}>
          {expandedContent}
        </div>
      ) : null}
    </div>
  );
}

export function SkillListExpandedSection({
  label,
  children,
  variant = 'default',
}: {
  label: string;
  children: ReactNode;
  variant?: SkillListVariant;
}) {
  const s = styles[variant];

  return (
    <>
      <div className={s.expandedLabel}>{label}</div>
      <div className={cn('rounded-[5px] p-2', s.expandedContent)}>{children}</div>
    </>
  );
}

export function SkillListExpandedHint({
  children,
  variant = 'default',
}: {
  children: ReactNode;
  variant?: SkillListVariant;
}) {
  return <p className={styles[variant].expandedText}>{children}</p>;
}

export function SkillListExpandedSubLabel({
  children,
  variant = 'default',
}: {
  children: ReactNode;
  variant?: SkillListVariant;
}) {
  return <div className={styles[variant].subLabelText}>{children}</div>;
}

export function SkillListExpandedTitle({
  children,
  variant = 'default',
}: {
  children: ReactNode;
  variant?: SkillListVariant;
}) {
  return <div className={styles[variant].titleText}>{children}</div>;
}

export function SkillListChevron({
  isExpanded,
  onClick,
  label,
  disabled,
  variant = 'default',
}: {
  isExpanded: boolean;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  variant?: SkillListVariant;
}) {
  const s = styles[variant];

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('shrink-0 p-1', s.chevronColor)}
      aria-label={label}
      aria-expanded={isExpanded}
      disabled={disabled}
    >
      {isExpanded ? <ChevronUp className={s.chevronSize} /> : <ChevronDown className={s.chevronSize} />}
    </button>
  );
}

export function SkillListExampleUsage({
  handle,
  children,
  variant = 'default',
}: {
  handle: string;
  children: ReactNode;
  variant?: SkillListVariant;
}) {
  const s = styles[variant];

  return (
    <p className={s.expandedText}>
      <span className={s.exampleHandle}>/{handle}</span> <span>{children}</span>
    </p>
  );
}
